import React, { useState, useEffect } from 'react';
import { X, MessageSquare } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface ReviewCommentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (comment: string) => void;
  decision: 'approved' | 'rejected' | null;
}

export const ReviewCommentModal: React.FC<ReviewCommentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  decision
}) => {
  const { t } = useTranslation();
  const [comment, setComment] = useState('');

  useEffect(() => {
    // Reset comment when modal opens
    if (isOpen) {
      setComment('');
    }
  }, [isOpen]);

  if (!isOpen || !decision) return null;

  const handleSubmit = () => {
    onSubmit(comment);
    // Keep modal open until submission is complete, let parent handle close
  };

  const modalTitle = decision === 'approved' 
    ? t('modals.reviewComment.titleApprove', 'Approve Post') 
    : t('modals.reviewComment.titleReject', 'Reject Post');
  
  const submitButtonText = decision === 'approved'
    ? t('modals.reviewComment.submitApprove', 'Submit Approval')
    : t('modals.reviewComment.submitReject', 'Submit Rejection');

  const submitButtonClass = decision === 'approved'
    ? 'bg-green-600 hover:bg-green-700'
    : 'bg-red-600 hover:bg-red-700';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 border border-black">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
             <MessageSquare className="w-5 h-5 mr-2 text-gray-500" />
             {modalTitle}
          </h3>
          <button 
            onClick={onClose}
            className="p-1 rounded-full text-gray-400 hover:bg-gray-100 hover:text-gray-600"
            aria-label={t('common.close')}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
             {t('modals.reviewComment.description', 'Add an optional comment for the writer.')}
          </p>
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={4}
            className="w-full px-3 py-2 text-gray-700 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            placeholder={t('modals.reviewComment.placeholder', 'Your comment...')}
          />
        </div>

        {/* Modal Footer */}
        <div className="flex items-center justify-end p-4 border-t border-gray-200 space-x-3">
          <button
            onClick={onClose}
            type="button"
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleSubmit}
            type="button"
            className={`px-4 py-2 text-sm font-medium text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-2 ${submitButtonClass} focus:ring-${decision === 'approved' ? 'green' : 'red'}-500`}
          >
            {submitButtonText}
          </button>
        </div>
      </div>
    </div>
  );
}; 